import React, {useMemo, useState} from 'react';
import {
  closestCenter,
  DndContext,
  DragOverlay,
  KeyboardSensor,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {restrictToVerticalAxis} from '@dnd-kit/modifiers';
import {
  arrayMove,
  SortableContext,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import {useFilters, usePagination, useSortBy, useTable} from 'react-table';
import {DraggableTableRow} from './DraggableTableRow';
import {StaticTableRow} from './StaticTableRow';
import _isUndefined from 'lodash/isUndefined';
import moment from 'moment';
import _isNull from 'lodash/isNull';
import {DateRangeFilter, DropdownFilter} from '../datatable/DataFilter';
import {Trans} from 'react-i18next';

export let forceFetch = null;

export default function DragTable({
  columns,
  data,
  setData,
  loading = false,
  initState = {},
  noDataMessage,
  onFetchData = null,
  storeParams = [],
}) {
  const [activeId, setActiveId] = useState();
  const items = useMemo(() => data.map(({id}) => id), [data]);
  const [manualData, setManualData] = useState([]);
  const [temp, setTemp] = useState(0);
  const processedColumns = columns.map(c => ({
    ...c,
    filter: getFilterType(c),
  }));
  forceFetch = () => setTemp(temp + 1);
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
  } = useTable(
    {
      columns: useMemo(() => processedColumns, [columns]),
      data: onFetchData ? manualData : data,
      filterTypes: useMemo(
        () => ({
          dateRange: (rows, id, filterValue) => {
            if (filterValue[0] === '' && filterValue[1] === '') return rows;
            return rows.filter(row => {
              const value = row.values[id];
              return _isUndefined(value) || value === null
                ? false
                : moment(row.values[id]).isBetween(
                    filterValue[0],
                    filterValue[1],
                    null,
                    '[]'
                  );
            });
          },
          dropdown: (rows, id, filterValue) => {
            if (!filterValue) return rows;
            return rows.filter(row => {
              const columnValue = row.values[id];
              if (!columnValue) return false;
              if (Array.isArray(columnValue)) {
                return columnValue.includes(filterValue) ? columnValue : false;
              }
              if (typeof columnValue === 'object') {
                return columnValue.value === filterValue;
              }
              return columnValue === filterValue;
            });
          },
          text: (rows, id, filterValue) => {
            if (!filterValue) return rows;
            return rows.filter(row => {
              const cellValue = row.values[id];
              if (_isNull(cellValue) || _isUndefined(cellValue)) return false;
              if (typeof cellValue === 'object') {
                return Object.keys(cellValue).some(
                  k =>
                    String(cellValue[k])
                      .toLowerCase()
                      .indexOf(String(filterValue).toLowerCase()) !== -1
                );
              }
              return (
                String(cellValue)
                  .toLowerCase()
                  .indexOf(String(filterValue).toLowerCase()) !== -1
              );
            });
          },
        }),
        // useGlobalFilter
        []
      ),
      disableSortRemove: true,
      disableMultiSort: true,
      initialState: {
        ...initState,
        sortBy: initState.sortBy
          ? !Array.isArray(initState.sortBy)
            ? [initState.sortBy]
            : initState.sortBy
          : [],
      },
      manualPagination: !!onFetchData,
    },
    useFilters,
    useSortBy,
    usePagination
  );

  const sensors = useSensors(
    useSensor(MouseSensor, {}),
    useSensor(TouchSensor, {}),
    useSensor(KeyboardSensor, {})
  );

  function handleDragStart(event) {
    setActiveId(event.active.id);
  }

  function handleDragEnd(event) {
    const {active, over} = event;
    if (active.id !== over.id) {
      const oldIndex = items.indexOf(active.id);
      const newIndex = items.indexOf(over.id);
      setData(arrayMove(data, oldIndex, newIndex));
    }

    setActiveId(null);
  }

  function handleDragCancel() {
    setActiveId(null);
  }

  const selectedRow = useMemo(() => {
    if (!activeId) {
      return null;
    }
    const row = rows.find(({original}) => original.id === activeId);
    prepareRow(row);
    return row;
  }, [activeId, rows, prepareRow]);

  return (
    <>
      <DndContext
        sensors={sensors}
        onDragEnd={handleDragEnd}
        onDragStart={handleDragStart}
        onDragCancel={handleDragCancel}
        collisionDetection={closestCenter}
        modifiers={[restrictToVerticalAxis]}
      >
        <table
          {...getTableProps()}
          className="table table-bordered table-hover dataTable"
        >
          <thead>
            {headerGroups.map((headerGroup, i) => (
              <tr {...headerGroup.getHeaderGroupProps()} key={`head-row-${i}`}>
                {headerGroup.headers.map(column => {
                  const {render, headerClass, style} = column;
                  return (
                    <th className={headerClass || ''} style={style}>
                      {render('Header')}
                    </th>
                  );
                })}
              </tr>
            ))}
          </thead>
          <tbody {...getTableBodyProps()}>
            <SortableContext
              items={items}
              strategy={verticalListSortingStrategy}
            >
              {rows.length === 0 && loading && (
                <Loading colSpan={columns.length} />
              )}
              {rows.length === 0 && !loading && (
                <NoData colSpan={columns.length} message={noDataMessage} />
              )}
              {rows.length > 0 &&
                rows.map((row, i) => {
                  prepareRow(row);
                  return (
                    <DraggableTableRow key={row.original.id} row={row} /> || (
                      <tr {...row.getRowProps()} key={`tr-${i}`}>
                        {row.cells.map((cell, ii) => {
                          return (
                            <td
                              {...cell.getCellProps({
                                className: cell.column.className || '',
                                style: cell.column.style || {},
                              })}
                              key={`tr${i}-td${ii}`}
                            >
                              {cell.render('Cell')}
                            </td>
                          );
                        })}
                      </tr>
                    )
                  );
                })}
            </SortableContext>
          </tbody>
        </table>
        <DragOverlay>
          {activeId && (
            <table style={{width: '100%'}}>
              <tbody>
                <StaticTableRow row={selectedRow} />
              </tbody>
            </table>
          )}
        </DragOverlay>
      </DndContext>
    </>
  );
}

function NoData({colSpan}) {
  return (
    <tr>
      <td colSpan={colSpan} className="text-center">
        <Trans i18nKey="c.table.filters.nodata">No Data</Trans>
      </td>
    </tr>
  );
}

function Loading({colSpan}) {
  return (
    <tr>
      <td colSpan={colSpan}>
        <div>Loading...</div>
      </td>
    </tr>
  );
}

function getFilterType(col) {
  if (col.filter) return col.filter;
  switch (col.Filter) {
    case DropdownFilter:
      return 'dropdown';
    case DateRangeFilter:
      return 'dateRange';
    default:
      return 'text';
  }
}
